import { createLoggerInterceptors } from '@devhacker/shared/libs/logger/createLoggerInterceptors';
import { API_TIMEOUT } from '@devhacker/shared/config/general';
import { WORDPRESS_API_URL } from '~/constants/config';
import { renameKeysToCamelCaseInPlace } from '~/utils/toCamelCase';
import type {
  FetchFilterIngredientsResponse,
  FetchFilterMenuResponse,
  FetchFilterSettingResponse,
  FetchRecipeListResponse,
  FetchRecipeResponse,
  FetchRecipesCountResponse,
  RecipeWidgetsResponse,
} from '~/types/api/recipes';

interface Params {
  $logger: any;
}

export const createRecipesApi = ({ $logger }: Params) => {
  const { onRequest, onResponse, onRequestError, onResponseError } = createLoggerInterceptors(
    $logger,
    'recipes',
  );

  const recipesRequest = $fetch.create({
    baseURL: `${WORDPRESS_API_URL}/api/recipes/v2`,
    timeout: API_TIMEOUT,
    parseResponse: JSON.parse, // без этого API может вернуть 200 код и html
    onRequest: [onRequest],
    onResponse: [
      onResponse,
      (config) =>
        // В отличии от основного лх тут мы не меняем формат который приходит из api
        // Но переименовываем ключи из snake_case в camelCase для удобства
        // Исключением является все что лежит внутри attributes блоков и parser_type, это нужно для совместимости с основным лх
        renameKeysToCamelCaseInPlace(config.response._data, ['parser_type', 'attributes']),
    ],
    onRequestError: [onRequestError],
    onResponseError: [onResponseError],
  });

  return {
    fetchRecipe: (pathname: string) => {
      return recipesRequest<FetchRecipeResponse>(`single-post/${pathname}`);
    },

    fetchRecipePreview: (id: number, preview_type: string, preview_type_hash: string) => {
      return recipesRequest<FetchRecipeResponse>(`preview/${id}`, {
        headers: {
          cookie: useRequestHeader('cookie') as string,
        },
        params: {
          preview_type,
          preview_type_hash,
        },
      });
    },

    fetchHomeRecipes: async (page: number, limit = 25) => {
      return recipesRequest<FetchRecipeListResponse>('home', {
        params: {
          page,
          limit,
        },
      });
    },

    fetchRecipesQuery: async (page: number, pathname: string, limit = 25) => {
      return recipesRequest<FetchRecipeListResponse>(encodeURI(`query${pathname}`), {
        params: {
          page,
          limit,
        },
      });
    },

    fetchRecipesCount: (pathname: string) => {
      return recipesRequest<FetchRecipesCountResponse>(`query${pathname}`, {
        query: { count: '' },
      });
    },

    fetchFilterMenus: () => {
      return recipesRequest<FetchFilterMenuResponse>('filter/menu');
    },

    fetchFilterSettings: (pathname: string) => {
      return recipesRequest<FetchFilterSettingResponse>(`filter/route${pathname}`);
    },

    searchIngredients: (searchValue: string) => {
      return recipesRequest<FetchFilterIngredientsResponse>(`filter/ingredients/${searchValue}`);
    },

    fetchRecipeWidgets: (pathname: string, includes: string[]) => {
      return recipesRequest<RecipeWidgetsResponse>('widgets', {
        params: {
          pathname,
          includes: includes.join(','),
        },
        // Override rename keys transfromer
        onResponse: [onResponse],
      });
    },

    fetchRecipeScripts: () => {
      return recipesRequest<TWidget[]>('scripts');
    },
  };
};
